import { AgenciesService, SubscriptionService } from '@services/requests';

type FormData = Record<string, string> | null;

const DIGIT_FIELDS = ['cpf', 'cnpj', 'phone', 'postal_code'] as const;
const TEXT_FIELDS = ['street', 'number', 'neighborhood', 'city', 'state', 'creci', 'complement',
  // IBGE municipality code from the CEP lookup; Payien needs it for the NFS-e tomador.
  'codigo_municipio'] as const;

/**
 * Strips all non-digit characters from a value.
 *
 * @param value - The raw input string.
 * @returns The digits-only string.
 */
function stripDigits(value: string): string { return (value || '').replace(/\D/g, ''); }

/**
 * Normalizes the account type, defaulting to `agency` for any non-solo value.
 *
 * @param type - The raw account type.
 * @returns Either `solo_broker` or `agency`.
 */
function resolveType(type?: string): string { return type === 'solo_broker' ? 'solo_broker' : 'agency'; }

/**
 * Copies digit-only and text fields from raw form data onto the payload.
 *
 * @param payload - The target request payload to mutate.
 * @param formData - The raw onboarding form data.
 */
function applyFields(payload: Record<string, string>, formData: Record<string, string>): void {
  for (const f of DIGIT_FIELDS) payload[f] = stripDigits(formData[f]);
  for (const f of TEXT_FIELDS) payload[f] = formData[f] || '';
}

/**
 * Builds the createAgency request payload from raw onboarding form data.
 *
 * @param formData - The raw onboarding form data.
 * @param type - The account type.
 * @returns The createAgency request payload.
 */
export function buildAgencyPayload(formData: Record<string, string>, type?: string) {
  const payload: Record<string, string> = { name: (formData.name || '').trim(), type: resolveType(type) };
  applyFields(payload, formData);
  return payload;
}

/**
 * The saved agency id, but only when it still belongs to the payer on screen.
 *
 * The id outlives the tab's memory: `usePersistedOnboarding` restores it from
 * sessionStorage, and `?agency_id=` can hand one over. The payer behind it
 * does not travel the same way, so a bare id with no key recorded against it
 * says nothing about the office now on screen -- and trusting it billed a new
 * buyer against the previous purchase's agency (BUG-021). `knownPayerKey` is
 * written only by `rememberAgency`, once this very flow created or was handed
 * the id, so its presence is the first thing checked here.
 *
 * It is then compared against the payer the form currently describes
 * (BUG-011): the saved draft was created for one payer, and if the buyer has
 * since changed the payer data -- a different document, phone, or address --
 * the draft no longer describes the office being bought, so a fresh agency
 * must be resolved rather than the stale one reused. An IDENTICAL payer, in
 * contrast, is a plain retry of the same attempt (a re-opened session, a
 * cycle flip) and keeps the one draft so no second agency is spawned.
 *
 * The guard against a draft spawning a duplicate on a genuinely new attempt
 * is `clearPersistedOnboarding`, run by `goToNewAgency()` before "+ Novo
 * escritório" starts over -- it clears the persisted id and key so this
 * returns null from the first keystroke of the new office.
 *
 * @param agencyId - The saved agency id, if any.
 * @param knownPayerKey - The payer the saved id was created for, if known.
 * @param payerKey - The payer the screen is about to charge.
 * @returns The id when it is reusable, or null to resolve one afresh.
 */
export function reusableAgencyId(
  agencyId: string | null | undefined,
  knownPayerKey: string | null | undefined,
  payerKey: string,
): string | null {
  if (!agencyId) return null;
  if (!knownPayerKey) return null;
  return knownPayerKey === payerKey ? agencyId : null;
}

export type ResolveAgencyArgs = {
  agencyId?: string | null;
  formData: FormData;
  type?: string;
  onAgencyCreated: (agencyId: string, name?: string) => void;
  onAgencyReused: (agencyId: string) => void;
};

/**
 * Extracts the existing agency id from an "already exists" API error.
 *
 * @param err - The caught API error.
 * @returns The existing agency id, or null when the error does not match.
 */
function extractAlreadyExistsId(err: any): string | null {
  const match = err?.status === 400 && err?.body?.title === 'AgencyAlreadyExistsError';
  return match ? err.body.agency_id ?? null : null;
}

/**
 * Creates the agency and patches its site when provided.
 *
 * @param formData - The raw onboarding form data.
 * @param type - The account type.
 * @returns The created agency id.
 */
async function createAndPatchAgency(formData: Record<string, string>, type?: string): Promise<string> {
  const response = await AgenciesService.createAgency(buildAgencyPayload(formData, type) as any);
  const id = (response as any)?.agency_id || (response as any)?.id || '';
  if (id && formData.site) await AgenciesService.updateAgency(id, { site: formData.site });
  return id;
}

/**
 * Resolves the agency id, creating the agency when only form data is present.
 *
 * @param args - The resolution arguments (existing id, form data, callbacks).
 * @returns The resolved agency id, or null/undefined when unavailable.
 */
export async function resolveAgencyId(args: ResolveAgencyArgs): Promise<string | null | undefined> {
  const { agencyId, formData, type, onAgencyCreated, onAgencyReused } = args;
  if (agencyId) return agencyId;
  if (!formData) return null;

  try {
    const resolved = await createAndPatchAgency(formData, type);
    onAgencyCreated(resolved, formData.name);
    return resolved;
  } catch (err: any) {
    const existingId = extractAlreadyExistsId(err);
    if (existingId) { onAgencyReused(existingId); return existingId; }
    throw err;
  }
}

/**
 * Creates the subscription checkout session, returning the checkout URL.
 *
 * The seat count is sent, never a price: the backend clamps it to what the
 * plan allows and works the amount out from there, so the browser can ask for
 * a different quantity but not for a different rate.
 *
 * @param planId - The selected plan id.
 * @param agencyId - The agency id the subscription belongs to.
 * @param seats - How many seats the buyer chose.
 * @param items - One line per plan, for a team that mixes tiers.
 * @param couponCode - A code the host has already checked, or nothing.
 * @returns The checkout/payment URL, or undefined when none is returned.
 */
/**
 * A coupon Payien refused, carrying the code the sentence is chosen from.
 *
 * Distinct from any other checkout failure on purpose: the buyer can act on
 * this one (use another code, or none), and the caller must not answer it by
 * rebuilding the agency and asking again -- the coupon would be refused the
 * same way and a second agency would exist for nothing (BUG-025).
 */
export class CheckoutCouponError extends Error {
  readonly errorCode: string;

  constructor(errorCode: string, message?: string) {
    super(message || errorCode);
    this.name = 'CheckoutCouponError';
    this.errorCode = errorCode;
  }
}

/**
 * The coupon refusal code an API error names, or null when it names none.
 *
 * The body travels differently depending on which layer wrapped it, so the
 * places it can hide are all read rather than assuming one shape.
 */
const couponRefusalCode = (err: unknown): string | null => {
  const seen = [
    (err as any)?.response?.data,
    (err as any)?.data,
    err,
  ];
  for (const body of seen) {
    const code = body?.error_code;
    if (typeof code === 'string' && code.startsWith('coupon_')) return code;
  }
  return null;
};

export async function createCheckoutUrl(
  planId: string, agencyId: string, seats?: number,
  items?: { plan_id: string; seats: number }[],
  couponCode?: string | null,
): Promise<string | undefined> {
  const redirectUrl = `${window.location.origin}/app/agencies?step=checkout`;
  const result = await SubscriptionService.createSubscription({
    plan_id: planId,
    agency_id: agencyId,
    redirect_url: redirectUrl,
    ...(seats == null ? {} : { seats }),
    // A team may mix tiers, so what is bought is a list. The amount is still
    // worked out from the catalogue on the server; this only says what.
    ...(items?.length ? { items } : {}),
    // The code, never a discount: Payien applies it when it builds the
    // session, and a smaller amount here would be refused as a price
    // mismatch against the catalogue.
    ...(couponCode ? { coupon_code: couponCode } : {}),
  } as any).catch((err: unknown) => {
    // Named here so the screen can say why. Left as a generic failure it
    // reached the buyer as "could not open payment", with the real reason
    // (a monthly-only coupon on a yearly plan) only in Payien's log.
    const code = couponRefusalCode(err);
    if (code) throw new CheckoutCouponError(code, (err as Error)?.message);
    throw err;
  });
  return result.checkout_url || result.payment_url;
}
